import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const SpecMaster = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState({
    spec_code: '',
    grade: '',
    carbon_min: '',
    carbon_max: '',
    manganese_min: '',
    manganese_max: '',
    phosphorus_min: '',
    phosphorus_max: '',
    sulphur_min: '',
    sulphur_max: '',
    silicon_min: '',
    silicon_max: '',
    chromium_min: '',
    chromium_max: '',
    nickel_min: '',
    nickel_max: '',
    molybdenum_min: '',
    molybdenum_max: '',
    aluminum_min: '',
    aluminum_max: '',
    vanadium_min: '',
    vanadium_max: '',
    status: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { api } = useAuth();

  // Fetch Spec Master data
  const { data: specData, isLoading, error, refetch } = useQuery(
    ['specMaster', page, rowsPerPage, searchTerm],
    async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/masters/specifications?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching specification data: ${error.message}`, { variant: 'error' });
      }
    }
  );

  // Create mutation
  const createMutation = useMutation(
    (data) => api.post('/masters/specifications', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('specMaster');
        enqueueSnackbar('Specification created successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error creating specification: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/masters/specifications/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('specMaster');
        enqueueSnackbar('Specification updated successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating specification: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => api.delete(`/masters/specifications/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('specMaster');
        enqueueSnackbar('Specification deleted successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting specification: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      carbon_min: formData.carbon_min ? parseFloat(formData.carbon_min) : null,
      carbon_max: formData.carbon_max ? parseFloat(formData.carbon_max) : null,
      manganese_min: formData.manganese_min ? parseFloat(formData.manganese_min) : null,
      manganese_max: formData.manganese_max ? parseFloat(formData.manganese_max) : null,
      phosphorus_min: formData.phosphorus_min ? parseFloat(formData.phosphorus_min) : null,
      phosphorus_max: formData.phosphorus_max ? parseFloat(formData.phosphorus_max) : null,
      sulphur_min: formData.sulphur_min ? parseFloat(formData.sulphur_min) : null,
      sulphur_max: formData.sulphur_max ? parseFloat(formData.sulphur_max) : null,
      silicon_min: formData.silicon_min ? parseFloat(formData.silicon_min) : null,
      silicon_max: formData.silicon_max ? parseFloat(formData.silicon_max) : null,
      chromium_min: formData.chromium_min ? parseFloat(formData.chromium_min) : null,
      chromium_max: formData.chromium_max ? parseFloat(formData.chromium_max) : null,
      nickel_min: formData.nickel_min ? parseFloat(formData.nickel_min) : null,
      nickel_max: formData.nickel_max ? parseFloat(formData.nickel_max) : null,
      molybdenum_min: formData.molybdenum_min ? parseFloat(formData.molybdenum_min) : null,
      molybdenum_max: formData.molybdenum_max ? parseFloat(formData.molybdenum_max) : null,
      aluminum_min: formData.aluminum_min ? parseFloat(formData.aluminum_min) : null,
      aluminum_max: formData.aluminum_max ? parseFloat(formData.aluminum_max) : null,
      vanadium_min: formData.vanadium_min ? parseFloat(formData.vanadium_min) : null,
      vanadium_max: formData.vanadium_max ? parseFloat(formData.vanadium_max) : null
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.spec_code, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      spec_code: item.spec_code,
      grade: item.grade || '',
      carbon_min: item.carbon_min || '',
      carbon_max: item.carbon_max || '',
      manganese_min: item.manganese_min || '',
      manganese_max: item.manganese_max || '',
      phosphorus_min: item.phosphorus_min || '',
      phosphorus_max: item.phosphorus_max || '',
      sulphur_min: item.sulphur_min || '',
      sulphur_max: item.sulphur_max || '',
      silicon_min: item.silicon_min || '',
      silicon_max: item.silicon_max || '',
      chromium_min: item.chromium_min || '',
      chromium_max: item.chromium_max || '',
      nickel_min: item.nickel_min || '',
      nickel_max: item.nickel_max || '',
      molybdenum_min: item.molybdenum_min || '',
      molybdenum_max: item.molybdenum_max || '',
      aluminum_min: item.aluminum_min || '',
      aluminum_max: item.aluminum_max || '',
      vanadium_min: item.vanadium_min || '',
      vanadium_max: item.vanadium_max || '',
      status: item.status || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      spec_code: '',
      grade: '',
      carbon_min: '',
      carbon_max: '',
      manganese_min: '',
      manganese_max: '',
      phosphorus_min: '',
      phosphorus_max: '',
      sulphur_min: '',
      sulphur_max: '',
      silicon_min: '',
      silicon_max: '',
      chromium_min: '',
      chromium_max: '',
      nickel_min: '',
      nickel_max: '',
      molybdenum_min: '',
      molybdenum_max: '',
      aluminum_min: '',
      aluminum_max: '',
      vanadium_min: '',
      vanadium_max: '',
      status: ''
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this specification?')) {
      deleteMutation.mutate(id);
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading specification data: {error.message}
        </Alert>
      </Box>
    );
  }

  const specifications = specData?.data?.specifications || specData?.data || [];
  const totalCount = specData?.data?.pagination?.total || 0;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Specification Master
      </Typography>

      {/* Search and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by code or grade..."
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Add Specification
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Spec Code</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Carbon (%)</TableCell>
              <TableCell>Manganese (%)</TableCell>
              <TableCell>Silicon (%)</TableCell>
              <TableCell>Chromium (%)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : specifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No specifications found
                </TableCell>
              </TableRow>
            ) : (
              specifications.map((spec) => (
                <TableRow key={spec.spec_code}>
                  <TableCell>{spec.spec_code}</TableCell>
                  <TableCell>{spec.grade}</TableCell>
                  <TableCell>
                    {spec.carbon_min && spec.carbon_max ? 
                      `${spec.carbon_min} - ${spec.carbon_max}` : 
                      spec.carbon_min || spec.carbon_max || '-'
                    }
                  </TableCell>
                  <TableCell>
                    {spec.manganese_min && spec.manganese_max ? 
                      `${spec.manganese_min} - ${spec.manganese_max}` : 
                      spec.manganese_min || spec.manganese_max || '-'
                    }
                  </TableCell>
                  <TableCell>
                    {spec.silicon_min && spec.silicon_max ? 
                      `${spec.silicon_min} - ${spec.silicon_max}` : 
                      spec.silicon_min || spec.silicon_max || '-'
                    }
                  </TableCell>
                  <TableCell>
                    {spec.chromium_min && spec.chromium_max ? 
                      `${spec.chromium_min} - ${spec.chromium_max}` : 
                      spec.chromium_min || spec.chromium_max || '-'
                    }
                  </TableCell>
                  <TableCell>{spec.status}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(spec)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(spec.spec_code)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingItem ? 'Edit Specification' : 'Add New Specification'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Spec Code"
                  value={formData.spec_code}
                  onChange={(e) => setFormData({ ...formData, spec_code: e.target.value.toUpperCase() })}
                  required
                  disabled={!!editingItem}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Chemical Composition (%)</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Carbon Min"
                  value={formData.carbon_min}
                  onChange={(e) => setFormData({ ...formData, carbon_min: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Carbon Max"
                  value={formData.carbon_max}
                  onChange={(e) => setFormData({ ...formData, carbon_max: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Manganese Min"
                  value={formData.manganese_min}
                  onChange={(e) => setFormData({ ...formData, manganese_min: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Manganese Max"
                  value={formData.manganese_max}
                  onChange={(e) => setFormData({ ...formData, manganese_max: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Silicon Min"
                  value={formData.silicon_min}
                  onChange={(e) => setFormData({ ...formData, silicon_min: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Silicon Max"
                  value={formData.silicon_max}
                  onChange={(e) => setFormData({ ...formData, silicon_max: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Chromium Min"
                  value={formData.chromium_min}
                  onChange={(e) => setFormData({ ...formData, chromium_min: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Chromium Max"
                  value={formData.chromium_max}
                  onChange={(e) => setFormData({ ...formData, chromium_max: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Nickel Min"
                  value={formData.nickel_min}
                  onChange={(e) => setFormData({ ...formData, nickel_min: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.0001"
                  label="Nickel Max"
                  value={formData.nickel_max}
                  onChange={(e) => setFormData({ ...formData, nickel_max: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value.toUpperCase() })}
                  inputProps={{ maxLength: 1 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SpecMaster;